import { useState } from "react";
import { Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { errMsg } from "../ui";
import { addDays } from "./meta";

function Check({ checked, onChange, label, hint, testid }) {
  return (
    <label className="flex items-start gap-2 text-sm text-slate-700">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid={testid} />
      <span><span className="font-medium">{label}</span>{hint && <span className="block text-[11px] text-slate-500">{hint}</span>}</span>
    </label>
  );
}

/** Manual run: collect a date range from WebCDR and/or re-check the open cycle's usage on Stax Bill. */
export function UsageRunForm({ ov, onStarted }) {
  const today = ov.utc_today;
  const yesterday = addDays(today, -1);
  const cycleEnd = ov.open_cycle.end < today ? ov.open_cycle.end : today;
  const [f, setF] = useState({ start: yesterday, end: yesterday, collect: true, check_only: false, usage: true, domains: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const run = async () => {
    setBusy(true);
    try {
      const domains = f.domains.split(/[\s,;]+/).filter(Boolean);
      await api.post("/usage-charges/runs", { ...f, domains });
      toast.success("Run started"); onStarted();
    } catch (e) { toast.error(errMsg(e, "Could not start the run")); } finally { setBusy(false); }
  };
  const live = ov.config.stax_live && f.usage && !f.check_only;
  return (
    <Card className="border-slate-200 p-4 shadow-sm" data-testid="uc-run-form">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-sm font-semibold text-slate-800">Run by hand</h3>
        <span className="text-xs text-slate-500">Any day up to today (UTC). Today stays partial until WebCDR publishes it tomorrow; collecting again only adds what's missing.</span>
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" variant="outline" onClick={() => setF({ ...f, start: today, end: today })} data-testid="uc-preset-today">Today</Button>
          <Button size="sm" variant="outline" onClick={() => setF({ ...f, start: yesterday, end: yesterday })} data-testid="uc-preset-yesterday">Yesterday</Button>
          <Button size="sm" variant="outline" onClick={() => setF({ ...f, start: ov.open_cycle.start, end: cycleEnd })} data-testid="uc-preset-cycle">Whole open cycle</Button>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-[auto_1fr_1fr]">
        <div className="flex items-start gap-2">
          <label className="space-y-1"><span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">From</span>
            <Input type="date" value={f.start} max={today} onChange={(e) => set("start")(e.target.value)} className="h-9 w-40" data-testid="uc-run-start" /></label>
          <label className="space-y-1"><span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">To</span>
            <Input type="date" value={f.end} max={today} onChange={(e) => set("end")(e.target.value)} className="h-9 w-40" data-testid="uc-run-end" /></label>
        </div>
        <div className="space-y-2">
          <Check checked={f.collect} onChange={set("collect")} label="Collect calls from WebCDR" hint="Saves the days to the billing database; calls already saved are skipped." testid="uc-run-collect" />
          <Check checked={f.check_only} onChange={set("check_only")} label="Check only — save nothing" hint="Counts what would be added, and the usage step stays a dry run." testid="uc-run-check-only" />
          <Check checked={f.usage} onChange={set("usage")} label={`Then total the open cycle (${ov.open_cycle.label}) and check Stax Bill`} hint={ov.config.stax_live ? "Stax Bill updates are ON — amounts are written." : "Dry run — shows what each subscription would get."} testid="uc-run-usage" />
        </div>
        <label className="space-y-1"><span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Only these domains (optional)</span>
          <Textarea value={f.domains} onChange={(e) => set("domains")(e.target.value)} rows={3} placeholder="all domains with calls" className="font-mono text-xs" data-testid="uc-run-domains" />
          <span className="block text-[11px] text-slate-500">Limits the Stax Bill step — handy for trying a live update on one domain first.</span></label>
      </div>
      <div className="mt-3 flex items-center justify-end gap-3">
        {live && <span className="text-xs font-semibold text-emerald-700" data-testid="uc-run-live-note">This run writes to Stax Bill.</span>}
        <Button onClick={run} disabled={busy || !!ov.running || (!f.collect && !f.usage) || !ov.connection} data-testid="uc-run-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Start run</Button>
      </div>
    </Card>
  );
}
